import { FC, useState } from 'react';
import styled from 'styled-components';
import delete_icon from '../../../assets/delete_icon.svg';
import { IStore } from '../../../store/shopping/interface';
import arrow_right_icon from '../../../assets/arrow_right_icon.svg';
import { useNavigate } from 'react-router-dom';
import { Carousel } from '../../../component/carousel';
import { useStoreStatusMutation } from '../../../store/shopping/api';
import { showErrorToast, showSuccessToast } from '../../../utils/toast';
import { pxToRem } from '../../../utils/pxToRem';

type IStoreListCard = {
  handleDelete: () => void;
  storeItem: IStore;
};
export const StoreListCard: FC<IStoreListCard> = ({
  storeItem,
  handleDelete
}) => {
  const navigate = useNavigate();
  const [storeOpen, setStoreOpen] = useState(storeItem.status);
  const [storeStatus, { isLoading: isStoreStatusLoading }] =
    useStoreStatusMutation();
  const handleView = () => {
    navigate(`/shopping/view/${storeItem.name}/${storeItem._id}`, {
      state: { storeId: storeItem._id }
    });
  };
  const handleStoreStatus = (id: string) => {
    if (isStoreStatusLoading) return;
    const body = {
      status: storeItem.status !== 'open' ? 'open' : 'closed'
    };
    setStoreOpen(storeItem.status !== 'open' ? 'open' : 'closed');
    storeStatus({ body, id })
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setStoreOpen(result?.data.status);
      })
      .catch((error) => {
        showErrorToast(error.message);
        setStoreOpen(storeItem.status);
      });
  };
  return (
    <Card>
      <StoreImage images={storeItem.file} />
      <DeleteBtn onClick={handleDelete}>
        <Icon src={delete_icon} />
      </DeleteBtn>
      <StoreDetails>
        <StoreHeader>
          <StoreName>{storeItem.name}</StoreName>
          <AvaliablityContainer>
            <Switch>
              <Input
                type="checkbox"
                readOnly
                checked={storeOpen === 'open'}
                onClick={() => handleStoreStatus(storeItem._id)}
              />
              <SliderRound id={'slider'} />
            </Switch>
            {isStoreStatusLoading
              ? 'Loading...'
              : storeItem.status === 'open'
              ? 'Open'
              : 'Closed'}
          </AvaliablityContainer>
        </StoreHeader>
        <StoreDescription>Stock: {storeItem.products}</StoreDescription>
        <ViewBtn onClick={handleView}>
          <p>View</p>
          <Icon src={arrow_right_icon} />
        </ViewBtn>
      </StoreDetails>
    </Card>
  );
};

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  position: relative;
  padding: 8px 8px 35px 8px;
  width: calc(30.33% - 16px);
  max-height: 498px;
  margin: 0px 10px;
  margin-bottom: 48px;
  @media (max-width: 1000px) {
    width: calc(45% - 16px);
    margin: 0px 15px;
    margin-bottom: 48px;
  }
  @media (max-width: 730px) {
    width: 100%;
    margin: 0;
    margin-bottom: 48px;
  }
`;

const StoreImage = styled(Carousel)`
  border-radius: 16px;
  width: 100%;
  height: 200px;
  object-fit: cover;
  background-color: #c4c4c4;
`;
const DeleteBtn = styled.button`
  position: absolute;
  right: 24px;
  top: 16px;
  background-color: transparent;
  outline: none;
  border: none;
  padding: 0;
  & :hover {
    opacity: 0.6;
  }
`;
const Icon = styled.img`
  max-height: 48px;
  max-width: 48px;
`;

const StoreDetails = styled.div`
  padding: 23px 16px 0px 16px;
`;

const StoreHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StoreName = styled.p`
  font-size: ${pxToRem(16)};
  font-style: normal;
  font-weight: 500;
  max-width: 146px;
  line-height: ${pxToRem(22)};
  letter-spacing: 0px;
  text-align: left;
  text-transform: capitalize;
`;
const StoreDescription = styled(StoreName)`
  font-size: ${pxToRem(14)};
  font-weight: 400;
  overflow: hidden;
  max-width: unset;
  text-align: right;
`;

const SliderRound = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 12px;
  ::before {
    border-radius: 50%;
    position: absolute;
    content: '';
    height: 12px;
    width: 12px;
    left: 4px;
    bottom: 2px;
    top: 2px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }
`;
const Input = styled.input`
  :focus + ${SliderRound} {
    box-shadow: 0 0 1px #2f7ff6;
  }
  :checked + ${SliderRound} {
    background-color: #2f7ff6;
    ::before {
      -webkit-transform: translateX(12px);
      -ms-transform: translateX(12px);
      transform: translateX(12px);
    }
  }
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 16px;
  margin-right: 6px;
  ${Input} {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const AvaliablityContainer = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  align-items: center;

  font-size: ${pxToRem(16)};
  font-style: normal;
  font-weight: 500;
  line-height: ${pxToRem(22)};
  letter-spacing: 0px;
  text-transform: capitalize;
`;
const ViewBtn = styled.button`
  font-style: normal;
  font-weight: 500;
  font-size: ${pxToRem(12)};
  line-height: 150%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  padding: 7px 12px;
  outline: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.sojiOrange};
  & :hover {
    opacity: 0.6;
  }
`;
